import React, { useContext, useEffect, useMemo, useState } from "react";
import { useColorModeValue, Stack, Text, Box, Tooltip, useBreakpointValue } from "@chakra-ui/react";
import { useWorkflowKey } from "hooks/useWorkflows";
import {
  useCollectionKey,
  useConfigMap,
  useGetViewConfig,
  useLatestCollectionWorkflowId,
  useProjectParams,
  useCollectionCreatedDate,
  useFilterAndGroupReports,
  useDashboardMetrics,
  useContents,
  useDealFinderTickers,
} from "hooks";
import { useLocation } from "react-router-dom";
import { formatDateWithOutputFormat } from "screens/common/modal/formatters";
import { ProjectHeaderImage } from "screens/landing/tabs/project/projectLandingTileLayouts/components/ProjectHeaderImage";
import { ContentPreview } from "screens/content/contentView/previewSection/ContentPreview";
import { ProjectAnalysisPopover } from "screens/landing/tabs/collections/chartComponents/ProjectAnalysisPopover";
import type { ContentDetails } from "types/content/ContentDetails";
import {
  useBuySellChartColors,
  createRatingConfig,
  getRatingInfo,
  parseBuySellRatingNumber,
} from "screens/landing/tabs/collections/chartComponents/data/buySellData";
import { StockIndicator } from "screens/landing/tabs/project/projectLandingTileLayouts/components/StockIndicator";
import { ContentFilterContext } from "screens/content";
import type { DashboardMetric } from "types/dashboardMetrics";
import type { StockEquityData } from "types/stock";
import type { DealFinderTicker } from "api/tickers/models/DealFinderTicker";

interface Props {
  collectionId: string;
}

export const ProjectDetailDefaultViewHeader = ({ collectionId }: Props) => {
  const { isPortfolios } = useProjectParams();
  const textColor = useColorModeValue("primary.darkGray", "gray.400");
  const { pathname } = useLocation();
  const configMap = useConfigMap();
  const isProject = pathname.includes("project");
  const route = isPortfolios ? "due_diligence" : pathname.split("/").slice(isProject ? 2 : 1, isProject ? 3 : 2)[0];
  const workflowIntentFilters = useGetViewConfig("workflowIntentFilters", route, configMap);
  const workflowId = useLatestCollectionWorkflowId(collectionId, workflowIntentFilters);
  const workflowStatus = useWorkflowKey(workflowId, "status");
  const collectionType = useCollectionKey(collectionId, "collectionType");
  const collectionName = useCollectionKey(collectionId, "name");
  const projectGroupId = useCollectionKey(collectionId, "projectGroupId");
  const projectMetadataIds = useCollectionKey(collectionId, "metadataIds");
  const isMobile = useBreakpointValue({ base: true, md: false }, { fallback: "md", ssr: false });
  const projectCreationDate = useCollectionCreatedDate(collectionId);
  const bgColorReadOnly = useColorModeValue("orange.100", "gray.700");
  const tileBorderColor = useColorModeValue("gray.300", "gray.600");
  const { availableContent } = useContext(ContentFilterContext);
  const contentDataFiltered = useFilterAndGroupReports(availableContent);
  const firstContentDetailFromFilter = useMemo(() => {
    const firstGroup = Object.values(contentDataFiltered)[0];
    return firstGroup?.[0] as ContentDetails | undefined;
  }, [contentDataFiltered]);

  const { getDealFinderTickers } = useDealFinderTickers();
  const [tickerData, setTickerData] = useState<DealFinderTicker | null>(null);
  const ticker = useCollectionKey(collectionId, "ticker");
  const exchange = useCollectionKey(collectionId, "stockExchange");

  // Fetch ticker data when ticker and exchange are available
  useEffect(() => {
    const fetchTickerData = async () => {
      if (ticker && exchange) {
        try {
          const data = await getDealFinderTickers({
            symbolWithExchange: `${exchange}:${ticker}`,
          });
          setTickerData(data.length > 0 ? data[0] : null);
        } catch (error) {
          console.error("Error fetching ticker data:", error);
          setTickerData(null);
        }
      }
    };

    fetchTickerData();
  }, [ticker, exchange, getDealFinderTickers]);

  const companyLogoData = useContents(projectMetadataIds, {
    refreshFromNetwork: true,
    filterByTags: ["company_logo"],
  });

  // TODO: change this to use tags
  const companyStockContentData = useContents(projectMetadataIds, {
    refreshFromNetwork: true,
    filterByName: "stock equity data",
  });

  const companyStockData = useMemo((): StockEquityData | null => {
    if (companyStockContentData && companyStockContentData.length > 0) {
      const dynamicData = companyStockContentData[0].dynamicData?.dynamic_data;

      if (!dynamicData) return null;

      if (dynamicData && typeof dynamicData === "object" && "data" in dynamicData) {
        return dynamicData as StockEquityData;
      }

      return null;
    }
    return null;
  }, [companyStockContentData]);
  const companyLongName = companyStockData?.data.chart.result[0].meta.longName || "";
  const companyTicker = companyStockData?.data.chart.result[0].meta.symbol || "";
  const companyExchange = companyStockData?.data.chart.result[0].meta.exchangeName || "";
  const isPrivateExchange = companyExchange.toUpperCase().includes("PRIVATE");

  const workflowStateMessage = useMemo(() => {
    if (workflowStatus) {
      if (workflowStatus === "complete") {
        return "Project completed";
      } else if (workflowStatus === "in_progress") {
        return "Project in progress";
      } else {
        return "Project incomplete";
      }
    }
    return "Project not started";
  }, [workflowStatus]);

  const shareDetails = useCollectionKey(collectionId, "shareDetails");

  const dashboardMetricsData = useDashboardMetrics(projectMetadataIds, "buy_sell");
  const buySellValue = dashboardMetricsData ? (dashboardMetricsData[0] as DashboardMetric) : undefined;
  const buySellScoreSentiment = useCollectionKey(collectionId, "buySellScoreSentiment");
  const legacyBuySellScore = buySellScoreSentiment?.value;
  const buySellValueToUse = buySellValue?.data?.[0] || Number(legacyBuySellScore);
  const numericRating = parseBuySellRatingNumber(buySellValueToUse);
  const chartColors = useBuySellChartColors();
  const ratingConfig = useMemo(() => createRatingConfig(chartColors), [chartColors]);
  const buySellData = getRatingInfo(numericRating, ratingConfig);

  console.log(tickerData, "Ticker Data");

  if (isMobile) {
    return (
      <Stack direction="row" className="project-detail-header" alignItems={"top"} justifyContent={"space-between"} width="100%">
        <Stack alignItems={"top"} width="100%">
          {collectionType && (
            <ProjectHeaderImage
              projectGroupId={projectGroupId}
              projectName={companyLongName}
              projectType={collectionType}
              width={isMobile ? "100%" : "20rem"}
              logoUrl={tickerData?.companyLogoUrl || undefined}
              logoOnly
              mediaId={companyLogoData?.[0]?.id}
            />
          )}
          {!isPrivateExchange && companyStockData && (
            <StockIndicator
              style={{ cursor: "default" }}
              companyTicker={companyTicker}
              companyStockTitle={`${companyLongName}: ${companyTicker.replace(".", "").split(":", 1)}`}
              companyStockExchange={companyExchange}
            />
          )}
          {buySellData.label !== "--" && (
            <Box
              position={"absolute"}
              top={isMobile ? "2.5rem" : "3.3rem"}
              bgColor={buySellData.color}
              textAlign={"center"}
              maxWidth="7.5rem"
              color={textColor}
              borderRadius="md"
              px="4px"
              py="2px"
              fontSize={isMobile ? "10px" : "xs"}
              fontWeight={"semibold"}
              width="100%">
              {buySellData.label.toUpperCase()}
            </Box>
          )}
        </Stack>
        {firstContentDetailFromFilter && (
          <ContentPreview
            thumbnailFit="fill"
            maxHeight="5rem"
            backgroundColor={"transparent"}
            itemContent={firstContentDetailFromFilter}
            thumbnailHeight={"125px"}
            thumbnailWidth={"85px"}
            noOnClickAction
          />
        )}
      </Stack>
    );
  }

  return (
    <Stack direction="row" className="project-detail-header" width="100%" justifyContent={"space-between"}>
      <Stack alignItems={"top"}>
        {collectionType && (
          <ProjectHeaderImage
            projectGroupId={projectGroupId}
            projectName={companyLongName}
            projectType={collectionType}
            width={isMobile ? "100%" : "20rem"}
            logoUrl={tickerData?.companyLogoUrl || undefined}
            logoOnly
            mediaId={companyLogoData?.[0]?.id}
          />
        )}
        {buySellData.label !== "--" && (
          <Box
            bgColor={buySellData.color}
            textAlign={"center"}
            maxWidth={"8rem"}
            color={textColor}
            borderRadius="md"
            px="0.5rem"
            py="0.2rem"
            fontSize=".7rem"
            fontWeight={"semibold"}
            width="100%">
            {buySellData.label.toUpperCase()}
          </Box>
        )}
      </Stack>
      <Stack direction="row" alignItems="top" spacing="1rem" width="100%" justifyContent="flex-end">
        {!isPrivateExchange && companyStockData && (
          <StockIndicator
            style={{ cursor: "default" }}
            companyTicker={companyTicker}
            companyStockTitle={`${companyLongName}: ${companyTicker.replace(".", "").split(":", 1)}`}
            companyStockExchange={companyExchange}
          />
        )}
        <Stack spacing="0">
          <Stack direction={"row"} alignItems="center" spacing="0.5rem">
            {isPrivateExchange && companyExchange && (
              <Text
                className="ch-project-stock-indicator-exchange"
                fontSize={isMobile ? "sm" : "md"}
                color={textColor}
                fontWeight="semibold">
                {companyExchange} :
              </Text>
            )}
            <Text
              className="ch-project-stock-indicator-company-name"
              fontSize={isMobile ? "sm" : "md"}
              color={textColor}
              fontWeight="semibold">
              {companyLongName.length > 0 ? companyLongName.toUpperCase() : collectionName?.toUpperCase()}
            </Text>
          </Stack>

          {projectCreationDate && (
            <Tooltip label="Project completed date">
              <Text textAlign={"right"} fontSize="xs" color={textColor}>
                {`As of ${
                  projectCreationDate
                    ? formatDateWithOutputFormat(new Date(projectCreationDate), "do MMM yyyy hh:mm")
                    : workflowStateMessage
                }`}
              </Text>
            </Tooltip>
          )}
          {shareDetails?.accessMode === "read" && (
            <Box
              bgColor={bgColorReadOnly}
              textAlign={"center"}
              width={"4.5rem"}
              fontSize="10px"
              mt="5px"
              px="6px"
              py="3px"
              fontWeight={"semibold"}
              borderRadius={"md"}>
              READ ONLY
            </Box>
          )}
        </Stack>
        {firstContentDetailFromFilter && (
          <Box position="relative" width="100%" maxW={"4rem"} className="ch-project-landing-report-preview-container">
            <ProjectAnalysisPopover projectId={collectionId}>
              <Box borderColor={tileBorderColor} borderWidth="1px" borderRadius="sm">
                <ContentPreview
                  thumbnailFit="fill"
                  maxHeight="4rem"
                  backgroundColor={"transparent"}
                  itemContent={firstContentDetailFromFilter}
                  thumbnailHeight={"120px"}
                  thumbnailWidth={"260px"}
                  noOnClickAction
                />
              </Box>
            </ProjectAnalysisPopover>
          </Box>
        )}
      </Stack>
    </Stack>
  );
};
